import { gt, gte, major, minor, patch } from "semver";
import { useToast, POSITION as TOAST_POSITION } from "vue-toastification";
import { Check } from "@lucide/vue";
import { useUser } from "@/api/user.store";
import { i18n } from "@/i18n";

import { useSimulationMessage } from "@core/ui/simulationMessage.store";
import envOptions from "@/helpers/Env";

import ToastUpdated from "@core/ui/ToastUpdated.vue";

import type { User } from "@/api/User";

/**
 * Check the current software version and display an update popup
 * if the version is used for the first time.
 */
export async function showUpdateNotificationIfNeeded() {
  const { getOrLoadUser, updateUser } = useUser();
  const toast = useToast();

  const url = new URL(window.location.href);

  if (url.pathname.includes("/models/public/") && url.pathname.includes("/view")) {
    // Don't show the update notification on a public model page.
    return;
  }

  let user: User | null = null;
  try {
    user = await getOrLoadUser({ showLoginPopup: false });
  } catch (error) {
    // If the user is not logged in, don't show the update notification.
    return;
  }

  // In localStorage, the last used version is saved. Load that version.
  const lastLocalVersion = localStorage.getItem("lastUsedAppVersion");
  const lastVersion = lastLocalVersion ?? user.lastUsedAppVersion ?? null;

  // Save the current app version to be able to check on the next launch
  // if an update happened between launches.
  localStorage.setItem("lastUsedAppVersion", envOptions.version);

  if (gt(envOptions.version, user?.lastUsedAppVersion || "0.0.0")) {
    // Update the user's last used version.
    updateUser({ lastUsedAppVersion: envOptions.version });
  }

  console.log(
    `last used version: ${lastVersion}`,
    `current version: ${envOptions.version}`
  );

  // Abort if the user has already used this version.
  const notUpdatedSinceLastUse =
    lastVersion != null && lastVersion != "" && gte(lastVersion, envOptions.version);
  if (notUpdatedSinceLastUse) {
    return;
  }

  // Get the last minor version: 1.34.5 ⇒ 1.34.0
  // and test if the user has seen the update notification for it.
  const lastMinorVersion =
    envOptions.version.split(".").slice(0, 2).join(".") + ".0";
  const hasSeenLastMinorVersion =
    lastVersion != null && gte(lastVersion, lastMinorVersion);

  // Load the text of the update notification; saved in the translation files.
  // Replace "." with "_", because the keys in the translation file would require nesting otherwise.
  // (e.g. 1.33.7 is stored as 1_33_7)
  const versionTranslationKey = hasSeenLastMinorVersion
    ? `${major(envOptions.version)}_${minor(envOptions.version)}_${patch(
        envOptions.version
      )}`
    : lastMinorVersion.replace(/\./g, "_");
  const newVersionTextExists = i18n.te(
    `updateMessages.${versionTranslationKey}`,
    "de"
  );

  // Show new version information if there is interesting new version info:
  if (newVersionTextExists) {
    const newVersionInfo = i18n.t(`updateMessages.${versionTranslationKey}`);

    const changelogLanguage = i18n.locale.value == "de" ? "de" : "en";

    const newVersionTitle = i18n.t(`updateTitles.${versionTranslationKey}`);

    const simulationMessage = useSimulationMessage();

    return simulationMessage.show({
      type: i18n.t("words.update", 1),
      title: newVersionTitle,
      class: `update-popup update-popup-${versionTranslationKey}`,
      item: {
        type: "text",
        text: newVersionInfo,
        webLink: {
          text: i18n.t("labels.viewChangelog"),
          url: `https://support.simcision.com/changelog-${changelogLanguage}/#changelog`
        }
      }
    });
  }
  // No new info saved in version? Then, just show a toast that informs that a
  // new version was installed with a link to the changelog (online).
  else {
    toast.info(ToastUpdated, {
      position: TOAST_POSITION.BOTTOM_RIGHT,
      timeout: false,
      icon: Check,
      closeOnClick: false
    });
  }
}
